import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { Badge, type BadgeTone } from './Badge';

export interface ScenarioCardProps {
  /** The condition label — "Good condition", "Poor condition". */
  tone: BadgeTone;
  toneLabel: string;
  /** The headline figure, already formatted. */
  amount: string;
  label: string;
  caption: ReactNode;
  isSelected: boolean;
  onSelect: () => void;
}

/**
 * One quick-range benchmark, selectable (plan §2.6).
 *
 * The card is a real `<button>` so the keyboard, the screen reader and the browser's own focus ring all
 * behave without being re-implemented; selection is a ring rather than a fill, because four filled
 * cards would spend colour on a state the salesperson can already see.
 */
export const ScenarioCard = ({ tone, toneLabel, amount, label, caption, isSelected, onSelect }: ScenarioCardProps) => (
  <button
    type="button"
    aria-pressed={isSelected}
    onClick={onSelect}
    className={cn(
      'flex flex-col items-start gap-1.5 rounded-lg border bg-card p-4 text-start transition-colors',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
      isSelected ? 'border-primary ring-1 ring-primary' : 'border-border hover:bg-muted',
    )}
  >
    <Badge tone={tone}>{toneLabel}</Badge>
    <span
      dir="ltr"
      className="text-xl font-semibold tabular-nums text-card-foreground"
    >
      {amount}
    </span>
    <span className="text-sm text-foreground">{label}</span>
    <span className="text-xs text-muted-foreground">{caption}</span>
  </button>
);
